import { useEffect } from "react";

/** Sets page-specific SEO metadata client-side. Server-side prerender is
 *  not part of this stage; the head is patched on mount so social crawlers
 *  that render JS + AI grounding services (which run headless Chromium)
 *  still pick up per-page titles + descriptions + canonical + OG.
 */
const CANON = "https://orangebysite.com";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).slice(0, 1).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function useSEO({ title, description, path, ogImage, jsonLd, noIndex }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) upsertMeta('meta[name="description"]', { name: "description", content: description });
    const canonical = `${CANON}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;
    upsertLink("canonical", canonical);
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "OrangeBySite" });
    if (title) upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    if (description) upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage || `${CANON}/hero-combined.png` });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    if (title) upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage || `${CANON}/hero-combined.png` });
    upsertMeta('meta[name="robots"]', { name: "robots", content: noIndex ? "noindex,nofollow" : "index,follow" });
    if (jsonLd) upsertJsonLd(`ld-${(path || "root").replace(/[^a-z0-9]+/gi, "-")}`, jsonLd);
  }, [title, description, path, ogImage, JSON.stringify(jsonLd), noIndex]);
}

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "OrangeBySite",
  legalName: "Love Exclusive L.L.C-FZ",
  alternateName: "OrangeBySite\u2122",
  url: CANON,
  logo: `${CANON}/orangebysite-logo.png`,
  sameAs: [],
  areaServed: ["United Kingdom", "United Arab Emirates"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Meydan Grandstand, 6th Floor, Meydan Road, Nad Al Sheba",
    addressLocality: "Dubai",
    addressCountry: "AE",
  },
  identifier: [
    { "@type": "PropertyValue", propertyID: "Meydan Free Zone Trade Licence", value: "2541994.01" },
    { "@type": "PropertyValue", propertyID: "Formation Number",               value: "2541994" },
  ],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "OrangeBySite",
  url: CANON,
};
